import React, { useState } from 'react';
import { observer } from 'src/utils/mobx-react';
import { DateTime } from 'src_common/utils/time/time';
import { useAppStateContext } from 'src/AppState/AppState';
import { DropdownSelect, components, DropdownIndicatorProps } from 'src/components/DropdownWrapper/DropdownWrapper';
import {
    TableWrapper,
    TableHeaderRow,
    TableRow,
    ColumnHeader,
    SortableColumnHeader,
    TableCell,
    ChevronDownWrapper,
    TableSelectCellClickable,
    SectionMessage,
    RowWithTable,
    InnerTable,
    TableRowAddColor,
    FilterWrapper,
    FilterLabel,
    FilterDropdownWrapper,
    getFilterDropdownStyles,
    FilterChevronIcon,
    KycStatusBadge,
    ExpandedDetailsWrapper,
    ExpandedDetailsTitle,
    JsonPreview,
    SmallText,
} from 'src/components/FraudAndCompliance/FraudAndCompliancesTab/FraudAndCompliances/components/IdMissionVerifications/IdMissionVerifications.style';
import {
    ChevronDownIconWrapper,
    Select,
    SelectWrapper,
    CustomerButtonWrapper,
    CustomerId,
    CustomerName,
} from 'src/components/FraudAndCompliance/FraudAndCompliancesTab/FraudAndCompliances/components/CommonTable.styles';
import {
    IdMissionVerificationsState,
    KycFilterOptionType,
    KYC_FILTER_OPTIONS,
    ImmutableItemType,
    DataType,
} from './IdMissionVerifications.state';

const ACTIONS = {
    your: [
        { action: 'cancel', name: 'Mark as reviewed' },
        { action: 'unassign', name: 'Unassign' },
    ],
    none: [{ action: 'assign', name: 'Assign to me' }],
    other: [{ action: 'unassign', name: 'Unassign' }],
};

interface OptionType {
    action: string;
    name: string;
}

const getOption = (option: OptionType, id: string): React.ReactElement => {
    return (
        <option
            key={`${id}-${option.action}`}
            value={option.action}
        >
            {option.name}
        </option>
    );
};

type ActionsType = 'your' | 'none' | 'other';

interface PropsType {
    type: ActionsType;
    status: string;
    onCancelRequest: (item: unknown) => void;
    onAssignRequest: (item: unknown) => void;
    onUnassignRequest: (item: unknown) => void;
    data: DataType;
}

const CustomDropdownIndicator = (props: DropdownIndicatorProps<KycFilterOptionType, false>): React.ReactElement => (
    <components.DropdownIndicator {...props}>
        <FilterChevronIcon />
    </components.DropdownIndicator>
);

export const IdMissionVerifications = observer('IdMissionVerifications', (props: PropsType): React.ReactElement => {
    const { reduxAction, common } = useAppStateContext();
    const [state] = useState(() => new IdMissionVerificationsState(common.trpcClient));

    state.ensureCustomerDataFetched(props.data);

    const renderRow = (item: ImmutableItemType, type: ActionsType): React.ReactNode => {
        const { id, accountId, createdAt, finalizedAt, overall, triggerReason, details } = state.getRowData(item);
        const isExpanded = state.isRowExpanded(id);
        const kycStatus = state.getKycStatus(accountId);

        return (
            <TableRow
                key={id}
                section={type}
                data-test={`id-mission-row-${accountId}`}
            >
                <RowWithTable colSpan={7}>
                    <InnerTable>
                        <tbody>
                            <TableRowAddColor>
                                <TableSelectCellClickable
                                    onClick={state.toggleRow(id)}
                                    data-test='id-mission-expand-button'
                                >
                                    <ChevronDownWrapper isCollapsed={isExpanded} />
                                </TableSelectCellClickable>
                                <TableCell data-test='id-mission-customer'>
                                    <CustomerButtonWrapper
                                        onClick={() => reduxAction.customersActionsOpenRequest(accountId)}
                                    >
                                        <CustomerName>{state.getCustomerName(accountId)}</CustomerName>
                                        <CustomerId>{accountId}</CustomerId>
                                    </CustomerButtonWrapper>
                                </TableCell>
                                <TableCell data-test='id-mission-date'>
                                    {createdAt === undefined
                                        ? '-'
                                        : DateTime.from(createdAt)?.format('DD-MM-YYYY HH:mm') ?? '-'}
                                    {finalizedAt === undefined ? null : (
                                        <>
                                            <br />
                                            <SmallText>
                                                Finalized:{' '}
                                                {DateTime.from(finalizedAt)?.format('DD-MM-YYYY HH:mm') ?? '-'}
                                            </SmallText>
                                        </>
                                    )}
                                </TableCell>
                                <TableCell data-test='id-mission-kyc-status'>
                                    <KycStatusBadge status={kycStatus}>{kycStatus}</KycStatusBadge>
                                </TableCell>
                                <TableCell data-test='id-mission-identity-process'>
                                    <KycStatusBadge status={overall}>{overall}</KycStatusBadge>
                                </TableCell>
                                <TableCell data-test='id-mission-trigger-reason'>{triggerReason}</TableCell>
                                <TableCell disableColor={true}>
                                    <SelectWrapper>
                                        <Select
                                            name='action'
                                            onChange={(e: React.SyntheticEvent): void =>
                                                state.doAction(e, item, {
                                                    onCancel: props.onCancelRequest,
                                                    onAssign: props.onAssignRequest,
                                                    onUnassign: props.onUnassignRequest,
                                                })
                                            }
                                            data-test='id-mission-action'
                                        >
                                            <option>Choose an action</option>
                                            {ACTIONS[type].map((actionOption) => getOption(actionOption, id))}
                                        </Select>
                                        <ChevronDownIconWrapper />
                                    </SelectWrapper>
                                </TableCell>
                            </TableRowAddColor>
                            {isExpanded ? (
                                <TableRowAddColor key={`${id}-expanded`}>
                                    <td></td>
                                    <td
                                        colSpan={6}
                                        className='table-row-opened'
                                    >
                                        <ExpandedDetailsWrapper>
                                            <ExpandedDetailsTitle>JSON Details:</ExpandedDetailsTitle>
                                            <JsonPreview>{JSON.stringify(details, null, 2)}</JsonPreview>
                                        </ExpandedDetailsWrapper>
                                    </td>
                                </TableRowAddColor>
                            ) : null}
                        </tbody>
                    </InnerTable>
                </RowWithTable>
            </TableRow>
        );
    };

    const { type, status, data } = props;
    const filteredData = state.getFilteredAndSortedData(data);
    const areNoItems = status === 'FINISHED' && filteredData.size === 0;

    return (
        <div>
            <FilterWrapper>
                <FilterLabel>KYC Status:</FilterLabel>
                <FilterDropdownWrapper>
                    <DropdownSelect<KycFilterOptionType>
                        options={KYC_FILTER_OPTIONS}
                        value={state.kycFilter}
                        onChange={state.setKycFilter}
                        customStyles={getFilterDropdownStyles<KycFilterOptionType, false>()}
                        components={{ DropdownIndicator: CustomDropdownIndicator }}
                        data-test='id-mission-filter-dropdown'
                    />
                </FilterDropdownWrapper>
            </FilterWrapper>

            {areNoItems ? (
                <SectionMessage>There are no items</SectionMessage>
            ) : (
                <TableWrapper>
                    <thead>
                        <TableHeaderRow>
                            <RowWithTable
                                as='th'
                                colSpan={7}
                            >
                                <InnerTable>
                                    <tbody>
                                        <TableRowAddColor>
                                            <ColumnHeader></ColumnHeader>
                                            <ColumnHeader>Customer</ColumnHeader>
                                            <SortableColumnHeader
                                                onClick={state.setDateSort}
                                                data-test='id-mission-sort-date'
                                            >
                                                Date and Time {state.sortDirection === 'date-desc' ? '▼' : '■'}
                                            </SortableColumnHeader>
                                            <ColumnHeader>KYC Status</ColumnHeader>
                                            <ColumnHeader>Identity Process</ColumnHeader>
                                            <SortableColumnHeader
                                                onClick={state.toggleSortDirection}
                                                data-test='id-mission-sort-reason'
                                            >
                                                Trigger Reason{' '}
                                                {state.sortDirection === 'reason-asc'
                                                    ? '▲'
                                                    : state.sortDirection === 'reason-desc'
                                                      ? '▼'
                                                      : '■'}
                                            </SortableColumnHeader>
                                            <ColumnHeader>Action</ColumnHeader>
                                        </TableRowAddColor>
                                    </tbody>
                                </InnerTable>
                            </RowWithTable>
                        </TableHeaderRow>
                    </thead>
                    <tbody>{filteredData.toArray().map((d) => renderRow(d, type))}</tbody>
                </TableWrapper>
            )}
        </div>
    );
});
